// FILE: DevServerControl.tsx
// Purpose: Thread-level control for publishing the worktree's dev server at a
// public preview URL — framework detection, start/stop, the shareable link, and
// enough of the process output to diagnose a boot failure without a terminal.
import type { DevServerFrameworkId, ThreadId } from "@taskin-code/contracts";
import { useCallback, useEffect, useState } from "react";

import { cn } from "../lib/utils";
import { formatPublicCountdown, resolvePreviewChip } from "./DevServerControl.logic";
import {
  CheckIcon,
  CopyIcon,
  ExternalLinkIcon,
  GlobeIcon,
  LoaderIcon,
  PlayIcon,
  StopIcon,
} from "~/lib/icons";
import { selectDevServerEntry, useDevServerStore } from "../devServerStore";
import { Popover, PopoverPopup, PopoverTrigger } from "./ui/popover";

interface DevServerControlProps {
  threadId: ThreadId;
  /**
   * Whether this account may publish the preview to the open internet
   * (`p.runtime.expose_public`). Without it the toggle is not rendered at all:
   * an inert switch invites asking why, and the answer is a permission the
   * person cannot grant themselves.
   */
  canPublish?: boolean;
}

export function DevServerControl({ threadId, canPublish = false }: DevServerControlProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [commandDraft, setCommandDraft] = useState<string | null>(null);

  const entry = useDevServerStore((state) => selectDevServerEntry(state, threadId));
  const load = useDevServerStore((state) => state.load);
  const start = useDevServerStore((state) => state.start);
  const stop = useDevServerStore((state) => state.stop);
  const setCommand = useDevServerStore((state) => state.setCommand);
  const setVisibility = useDevServerStore((state) => state.setVisibility);

  // Detection reads the worktree from disk, so it runs when the panel opens
  // rather than for every thread in the sidebar.
  useEffect(() => {
    if (open) void load(threadId);
  }, [open, threadId, load]);

  const status = entry.info?.status ?? null;
  const state = status?.state ?? "idle";
  // Re-read on every render rather than kept in state: the only thing that
  // moves it is the expiry push from the server, which re-renders anyway.
  const publicUntil = status?.publicUntil ?? entry.info?.publicUntil ?? null;
  const presentation = resolvePreviewChip({ state, publicUntil, nowMs: Date.now() });
  const publicCountdown = formatPublicCountdown(publicUntil, Date.now());
  const url = status?.url ?? (entry.info?.hostname ? `https://${entry.info.hostname}` : null);
  const isRunning = state === "running" || state === "booting";

  const detections = entry.info?.detections ?? [];
  const primary = detections[0] ?? null;
  // Not memoised on purpose: `detections` is a fresh array each render, so a
  // useMemo keyed on it would recompute anyway while pretending not to.
  const notes = detections.flatMap((detection) => detection.notes);

  const effectiveCommand = commandDraft ?? entry.info?.resolvedCommand ?? "";
  const commandDirty =
    commandDraft !== null && commandDraft.trim() !== (entry.info?.resolvedCommand ?? "").trim();

  const handleCopy = useCallback(() => {
    if (url === null) return;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1_500);
    });
  }, [url]);

  const handleToggle = useCallback(
    (frameworkId?: DevServerFrameworkId) => {
      if (isRunning) {
        void stop(threadId);
        return;
      }
      void start({ threadId, frameworkId: frameworkId ?? null });
    },
    [isRunning, start, stop, threadId],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[length:var(--app-font-size-ui-xs,10px)] font-normal text-muted-foreground/70 transition-colors hover:text-foreground/80"
        title="Publish this worktree at a preview URL"
      >
        <span className={cn("size-1.5 rounded-full", presentation.dot)} />
        <GlobeIcon className="size-3" />
        {presentation.label}
      </PopoverTrigger>

      <PopoverPopup className="w-96 p-0">
        <div className="space-y-3 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium">Preview</div>
              <div className="truncate text-xs text-muted-foreground">
                {primary ? primary.label : "No framework detected"}
              </div>
            </div>
            <button
              type="button"
              className={cn(
                "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors",
                isRunning
                  ? "bg-destructive/10 text-destructive hover:bg-destructive/20"
                  : "bg-primary text-primary-foreground hover:bg-primary/90",
                "disabled:pointer-events-none disabled:opacity-50",
              )}
              disabled={entry.pending || (!isRunning && effectiveCommand.trim() === "")}
              onClick={() => handleToggle()}
            >
              {entry.pending ? (
                <LoaderIcon className="size-3 animate-spin" />
              ) : isRunning ? (
                <StopIcon className="size-3" />
              ) : (
                <PlayIcon className="size-3" />
              )}
              {isRunning ? "Stop" : "Start"}
            </button>
          </div>

          {url !== null ? (
            <div className="flex items-center gap-1 rounded border border-border/60 bg-muted/30 px-2 py-1">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "min-w-0 flex-1 truncate text-xs",
                  state === "running"
                    ? "text-foreground hover:underline"
                    : "pointer-events-none text-muted-foreground",
                )}
              >
                {url.replace(/^https:\/\//, "")}
              </a>
              <button
                type="button"
                className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
                onClick={handleCopy}
                title="Copy preview URL"
              >
                {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
              </button>
              {state === "running" ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
                  title="Open preview"
                >
                  <ExternalLinkIcon className="size-3" />
                </a>
              ) : null}
            </div>
          ) : null}

          {/* No hostname means previews are not configured on this server, and
              a publish toggle over nothing to publish is just a puzzle. */}
          {canPublish && url !== null ? (
            <div
              className={cn(
                "space-y-1.5 rounded border px-2 py-2",
                presentation.isPublic
                  ? "border-amber-500/40 bg-amber-500/5"
                  : "border-border/60 bg-muted/20",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] font-medium">Public access</div>
                  <div className="text-[11px] leading-snug text-muted-foreground">
                    {presentation.isPublic
                      ? `Anyone with the link can reach this preview. Closes in ${publicCountdown}.`
                      : "Let apps without a Taskin account call this preview. Expires after 24 h."}
                  </div>
                </div>
                <button
                  type="button"
                  className={cn(
                    "shrink-0 rounded px-2 py-1 text-[11px] font-medium transition-colors",
                    presentation.isPublic
                      ? "bg-destructive/10 text-destructive hover:bg-destructive/20"
                      : "bg-primary text-primary-foreground hover:bg-primary/90",
                    "disabled:pointer-events-none disabled:opacity-50",
                  )}
                  disabled={entry.pending}
                  onClick={() => void setVisibility(threadId, !presentation.isPublic)}
                >
                  {presentation.isPublic ? "Make private" : "Publish"}
                </button>
              </div>
              {presentation.isPublic ? (
                <div className="flex items-center gap-2">
                  {/* Extending restarts the window from now rather than adding
                      to it, which is also what the server does. */}
                  <button
                    type="button"
                    className="rounded border border-border/60 px-2 py-0.5 text-[11px] transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
                    disabled={entry.pending}
                    onClick={() => void setVisibility(threadId, true)}
                  >
                    Extend 24 h
                  </button>
                  <span className="text-[11px] text-muted-foreground">
                    The dev server is unhardened — publish only what you can afford to expose.
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-1">
            <label className="text-[11px] font-medium text-muted-foreground" htmlFor="dev-command">
              Command
            </label>
            <div className="flex items-center gap-1">
              <input
                id="dev-command"
                className="min-w-0 flex-1 rounded border border-border/60 bg-background px-2 py-1 font-mono text-[11px] outline-none focus:border-primary/60"
                value={effectiveCommand}
                placeholder="php artisan serve --port=$PORT"
                spellCheck={false}
                onChange={(event) => setCommandDraft(event.target.value)}
              />
              {commandDirty ? (
                <button
                  type="button"
                  className="shrink-0 rounded bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                  onClick={() => {
                    void setCommand(threadId, commandDraft);
                    setCommandDraft(null);
                  }}
                >
                  Save
                </button>
              ) : null}
            </div>
            {entry.info?.commandOverride != null ? (
              <button
                type="button"
                className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => {
                  void setCommand(threadId, null);
                  setCommandDraft(null);
                }}
              >
                Reset to detected command
              </button>
            ) : null}
          </div>

          {/* More than one framework matched — a Laravel repo with a Vite
              front-end is the common case. Let the user pick explicitly. */}
          {detections.length > 1 && !isRunning ? (
            <div className="space-y-1">
              <div className="text-[11px] font-medium text-muted-foreground">Or start</div>
              <div className="flex flex-wrap gap-1">
                {detections.slice(1).map((detection) => (
                  <button
                    key={detection.frameworkId}
                    type="button"
                    className="rounded border border-border/60 px-2 py-0.5 text-[11px] transition-colors hover:bg-accent"
                    disabled={entry.pending}
                    onClick={() => handleToggle(detection.frameworkId)}
                  >
                    {detection.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {status?.error != null ? (
            <div className="rounded border border-red-500/30 bg-red-500/5 px-2 py-1.5 text-[11px] text-red-600 dark:text-red-400">
              {status.error}
            </div>
          ) : null}

          {entry.error !== null ? (
            <div className="rounded border border-red-500/30 bg-red-500/5 px-2 py-1.5 text-[11px] text-red-600 dark:text-red-400">
              {entry.error}
            </div>
          ) : null}

          {notes.length > 0 ? (
            <ul className="space-y-1">
              {notes.map((note) => (
                <li key={note} className="text-[11px] leading-snug text-muted-foreground">
                  {note}
                </li>
              ))}
            </ul>
          ) : null}

          {entry.status === "loaded" && detections.length === 0 && entry.info?.hostname === null ? (
            <p className="text-[11px] leading-snug text-muted-foreground">
              Previews are not configured on this server. Set TASKIN_PREVIEW_ZONE to the DNS zone
              previews are served from.
            </p>
          ) : null}
        </div>
      </PopoverPopup>
    </Popover>
  );
}

export default DevServerControl;
